import { useState, useEffect, useRef, useCallback } from 'react';
import { pb } from '../../../lib/pocketbase';
import { Audit, AuditWithDetails, IstZustandMassnahme } from '../types';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

async function retryOperation<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    throw error;
  }
}

// Custom Hook zur Verwaltung von Audit-Daten
export const useAudits = () => {
  const [audits, setAudits] = useState<AuditWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchMassnahmen = async (auditId: string): Promise<IstZustandMassnahme[]> => {
    if (!mountedRef.current) return [];
    try {
      const result = await retryOperation(() => 
        pb.collection('ist_zustand_massnahmen').getList(1, 50, {
          filter: `audit = "${auditId}"`,
          sort: '-created',
        })
      );
      return result.items as IstZustandMassnahme[];
    } catch (error) {
      console.error(`Error fetching massnahmen for audit ${auditId}:`, error);
      return [];
    }
  };

  const getKontrollpunkteCount = (audit: Audit): number => {
    return audit.kontrollpunkt?.length || 0;
  };

  const fetchAudits = useCallback(async () => {
    if (!mountedRef.current) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const records = await retryOperation(() => 
        pb.collection('audits').getList<Audit>(1, 50, {
          sort: '-created',
          expand: 'dienststelle,dienststelle.direktion,bereich,auditor',
          fields: `
            id,
            auditnummer,
            auditdatum,
            status,
            begleitpersonen,
            adresse,
            bereich,
            kontrollpunkt,
            auditor,
            dienststelle,
            expand.dienststelle.id,
            expand.dienststelle.name,
            expand.dienststelle.direktion,
            expand.dienststelle.expand.direktion.id,
            expand.dienststelle.expand.direktion.name,
            expand.bereich.id,
            expand.bereich.name,
            expand.bereich.adresse,
            expand.auditor.id,
            expand.auditor.name,
            expand.auditor.email
          `
        })
      );

      if (!mountedRef.current) return;

      // Fetch all unique auditor IDs
      const allAuditorIds = new Set<string>();
      records.items.forEach(audit => {
        if (Array.isArray(audit.auditor)) {
          audit.auditor.forEach(id => allAuditorIds.add(id));
        }
      });

      // Fetch all auditors in one batch
      const auditorMap = new Map<string, any>();
      if (allAuditorIds.size > 0) {
        try {
          const auditorFilter = Array.from(allAuditorIds).map(id => `id="${id}"`).join(' || ');
          const auditorsResult = await retryOperation(() =>
            pb.collection('users').getList(1, 100, {
              filter: auditorFilter,
              fields: 'id,name,email,avatar,collectionId,collectionName'
            })
          );
          auditorsResult.items.forEach(auditor => {
            auditorMap.set(auditor.id, auditor);
          });
        } catch (error) {
          console.error('Error fetching auditors:', error);
        }
      }

      const processedAudits: AuditWithDetails[] = [];
      const batchSize = 5;

      for (let i = 0; i < records.items.length; i += batchSize) {
        if (!mountedRef.current) return;

        const batch = records.items.slice(i, i + batchSize);
        const batchPromises = batch.map(async (audit) => {
          if (!mountedRef.current) return null;
          try {
            const massnahmen = await retryOperation(() => fetchMassnahmen(audit.id));
            
            // Manually populate expand.auditor from auditorMap
            const expandedAuditors = Array.isArray(audit.auditor) 
              ? audit.auditor.map(id => auditorMap.get(id)).filter(Boolean)
              : [];
            
            const auditWithDetails: AuditWithDetails = {
              ...audit,
              expand: {
                ...audit.expand,
                auditor: expandedAuditors.length > 0 ? expandedAuditors : undefined
              },
              auditor_display: expandedAuditors
                .map(a => a?.name || a?.email || 'Unbekannt')
                .filter(Boolean)
                .join(', '),
              massnahmen,
              kontrollpunkteCount: getKontrollpunkteCount(audit),
            };
            
            return auditWithDetails;
          } catch (error) {
            console.error(`Error processing audit ${audit.id}:`, error);
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        if (!mountedRef.current) return;

        const validResults = batchResults.filter((result): result is AuditWithDetails => result !== null);
        processedAudits.push(...validResults);
      }

      if (mountedRef.current) {
        setAudits(processedAudits);
      }
    } catch (error) {
      if (mountedRef.current) {
        console.error('Error fetching audits:', error);
        setError('Fehler beim Laden der Audits. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchAudits();

    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchAudits]);

  const deleteAudit = async (auditId: string) => {
    try {
      await retryOperation(() => pb.collection('audits').delete(auditId));
      await fetchAudits();
    } catch (error) {
      console.error('Error deleting audit:', error);
      setError('Fehler beim Löschen des Audits.');
    }
  };

  return {
    audits,
    loading,
    error,
    refreshAudits: fetchAudits,
    deleteAudit,
  };
};
